import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Portales que esta sesión NO ve, aunque su rol los alcance.
 *
 * El acceso a un portal sigue siendo del rol. Lo que esto añade es la
 * excepción: un portal marcado como "individualizar por usuario" solo lo ven
 * los que están en su lista (`menus_usuarios_acceso`). La regla RESTA — nadie
 * gana aquí un portal que su rol no tiene — y vive completa en la base, en
 * `get_menus_bloqueados()`: son tres los hooks que la consumen y con el
 * criterio repetido tres veces acabarían discrepando.
 *
 * La respuesta se comparte entre los tres. Sin esto, cada pantalla dispara su
 * propia consulta —el sidebar, la ruta y el permiso de la vista se resuelven a
 * la vez— y son tres viajes idénticos por render.
 */

let cache: { version: number; promesa: Promise<Set<number>> } | null = null;

async function pedirBloqueados(): Promise<Set<number>> {
  const { data, error } = await (supabase as any).rpc('get_menus_bloqueados');
  if (error) {
    // Sin respuesta NO se bloquea nada. Es deliberado: el rol ya filtró lo que
    // esta sesión puede ver, y fallar hacia "todo bloqueado" apagaría el menú
    // entero por un error de red.
    console.error('get_menus_bloqueados:', error);
    return new Set();
  }
  return new Set(((data ?? []) as { menu_id: number }[]).map((r) => Number(r.menu_id)));
}

/**
 * La misma respuesta, para quien la necesita dentro de un fetch y no como
 * estado: los tres hooks de permisos resuelven en callbacks y meterlos a
 * depender del ciclo de vida de otro hook los obligaría a re-ejecutarse.
 */
export async function obtenerMenusBloqueados(version: number): Promise<Set<number>> {
  if (!cache || cache.version !== version) {
    cache = { version, promesa: pedirBloqueados() };
  }
  return cache.promesa;
}

/** Invalida la caché compartida. La usa quien cambia la individualización. */
export function invalidarMenusBloqueados() {
  cache = null;
}

export function useMenusBloqueados() {
  const { user, permissionVersion } = useAuth();
  const [bloqueados, setBloqueados] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!user) {
      setBloqueados(new Set());
      setIsLoading(false);
      return;
    }
    try {
      setBloqueados(await obtenerMenusBloqueados(permissionVersion));
    } finally {
      setIsLoading(false);
    }
  }, [user, permissionVersion]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { menusBloqueados: bloqueados, isLoading };
}
